/**
 * Campaign countdown banner.
 *
 * Counts down to a launch instant, flips itself to the "live" message the moment
 * that instant passes, and removes itself once the campaign end date is reached.
 * Dismissals are remembered in localStorage so the bar stays gone for a while
 * instead of nagging on every page view.
 *
 * The launch/end instants arrive as unix timestamps rendered by Liquid, so the
 * shop timezone is resolved server-side and the browser never has to parse a
 * date string (Safari is unreliable at that).
 */

const SECOND = 1000;
const DAY = 86400 * SECOND;

const pad = (value) => (value < 10 ? `0${value}` : String(value));

class CampaignBanner extends HTMLElement {
  connectedCallback() {
    this.launchAt = Number(this.dataset.launchAt) * SECOND;
    this.endAt = Number(this.dataset.endAt || 0) * SECOND;
    this.storageKey = this.dataset.storageKey || '';
    this.dismissDays = Number(this.dataset.dismissDays || 0);
    this.wrapper = document.getElementById(this.dataset.wrapperId) || this;

    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.messageEl = this.querySelector('[data-message]');
    this.timerEl = this.querySelector('[data-timer]');
    this.ctaEl = this.querySelector('[data-cta]');
    this.statusEl = this.querySelector('[data-status]');

    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    const dismissButton = this.querySelector('[data-dismiss]');
    if (dismissButton) {
      this.onDismiss = this.dismiss.bind(this);
      dismissButton.addEventListener('click', this.onDismiss);
      this.dismissButton = dismissButton;
    }

    if (this.ctaEl) {
      this.onCtaClick = () => this.track('campaign_banner_click');
      this.ctaEl.addEventListener('click', this.onCtaClick);
    }

    this.tick();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.dismissButton && this.onDismiss) {
      this.dismissButton.removeEventListener('click', this.onDismiss);
    }
    if (this.ctaEl && this.onCtaClick) {
      this.ctaEl.removeEventListener('click', this.onCtaClick);
    }
  }

  onVisibilityChange() {
    // No point burning a timer on a backgrounded tab — resync on return instead.
    if (document.hidden) {
      clearTimeout(this.timer);
    } else {
      this.tick();
    }
  }

  tick() {
    const now = Date.now();

    if (this.endAt && now >= this.endAt) {
      clearTimeout(this.timer);
      this.wrapper.remove();
      return;
    }

    const remaining = this.launchAt - now;

    if (remaining <= 0) {
      clearTimeout(this.timer);
      this.setLive();
      // Once live there are no digits to update, but keep a slow poll alive when an
      // end date is set so a long-open tab still retires the bar on time.
      if (this.endAt) this.timer = setTimeout(() => this.tick(), 60 * SECOND);
      return;
    }

    const total = Math.floor(remaining / SECOND);
    this.render({
      days: Math.floor(total / 86400),
      hours: Math.floor(total / 3600) % 24,
      minutes: Math.floor(total / 60) % 60,
      seconds: total % 60,
    });

    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    // Land on the next whole second rather than drifting by a few ms per tick.
    this.timer = setTimeout(() => this.tick(), SECOND - (Date.now() % SECOND));
  }

  render(parts) {
    Object.keys(parts).forEach((unit) => {
      const el = this.units[unit];
      if (!el) return;
      const value = pad(parts[unit]);
      if (el.textContent !== value) el.textContent = value;
    });
  }

  /** Swap to the live message for anyone who had the page open when the campaign opened. */
  setLive() {
    if (this.classList.contains('campaign-banner--live')) return;
    this.classList.add('campaign-banner--live');

    if (this.timerEl) this.timerEl.hidden = true;
    if (this.messageEl && this.dataset.liveMessage) {
      this.messageEl.textContent = this.dataset.liveMessage;
    }
    const ctaLabel = this.ctaEl && this.ctaEl.querySelector('[data-cta-label]');
    if (ctaLabel && this.dataset.liveCta) {
      ctaLabel.textContent = this.dataset.liveCta;
    }
    if (this.statusEl && this.dataset.liveMessage) {
      this.statusEl.textContent = this.dataset.liveMessage;
    }
  }

  dismiss() {
    this.track('campaign_banner_dismiss');
    clearTimeout(this.timer);

    if (this.storageKey && this.dismissDays > 0 && !(window.Shopify && window.Shopify.designMode)) {
      try {
        localStorage.setItem(this.storageKey, String(Date.now() + this.dismissDays * DAY));
      } catch (error) {
        // Private browsing or a full quota — dismissing for this page view is enough.
      }
    }

    const wrapper = this.wrapper;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      wrapper.remove();
      return;
    }

    wrapper.style.height = `${wrapper.offsetHeight}px`;
    wrapper.classList.add('campaign-banner-wrapper--closing');
    // Force the start height to be committed. Without this the browser collapses
    // auto -> 0 in a single step, so nothing animates and transitionend never fires.
    void wrapper.offsetHeight;
    wrapper.style.height = '0px';

    const remove = () => wrapper.remove();
    wrapper.addEventListener('transitionend', remove, { once: true });
    // Belt and braces: never strand a half-collapsed bar if the transition is skipped.
    setTimeout(remove, 400);
  }

  track(event) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    window.dataLayer.push({ event, banner_id: this.dataset.wrapperId || '' });
  }
}

if (!customElements.get('campaign-banner')) {
  customElements.define('campaign-banner', CampaignBanner);
}
